import { useQuery } from '@tanstack/react-query';
import { knetTerminalAPI } from '@matix/api-client';
import type { KnetTerminalTillConfig, KnetTerminalTxn } from '@matix/types';

// KNET card terminal at the till (KNET_TERMINAL.md) — the pure decisions the
// PaymentModal and the KnetTerminalDialog share, plus the drawer config query.

/** Per tenant + drawer: two cashiers on one browser must not share a drawer's config. */
export function knetTerminalConfigKey(scope: string) {
  return ['knet-terminal-config', scope] as const;
}

const DISABLED: KnetTerminalTillConfig = {
  enabled: false,
  contract_verified: false,
  payment_type_codes: [],
  payment_type_id: null,
  terminal_name: null,
  timeout_seconds: null,
  allow_manual_fallback: true,
  refunds_enabled: false,
};

/**
 * The signed-in drawer's terminal config, once per session. Any failure — an
 * older pos-api without the route, a tenant without migration 073, a network
 * blip — reads as DISABLED, i.e. the till rings KNET manually as it always has.
 */
export function useKnetTerminalConfig(scope: string | null): KnetTerminalTillConfig {
  const q = useQuery({
    queryKey: knetTerminalConfigKey(scope ?? 'none'),
    queryFn: async () => {
      try {
        return await knetTerminalAPI.config();
      } catch {
        return DISABLED;
      }
    },
    enabled: scope != null,
    staleTime: 10 * 60 * 1000,
    gcTime: Infinity,
    refetchOnWindowFocus: false,
    retry: 0,
  });
  return q.data ?? DISABLED;
}

/** Does this tender drive the terminal on this drawer? */
export function isTerminalTender(cfg: KnetTerminalTillConfig, typeCode: number): boolean {
  return cfg.enabled && cfg.payment_type_codes.includes(typeCode);
}

export type TerminalStep = 'waiting' | 'approved' | 'failed';

/** What the dialog does with the latest poll. */
export function stepFor(txn: Pick<KnetTerminalTxn, 'status'>): TerminalStep {
  if (txn.status === 'queued' || txn.status === 'sent') return 'waiting';
  if (txn.status === 'approved') return 'approved';
  return 'failed';
}

/** One-line card summary for the applied-splits list and the receipt toast. */
export function cardSummary(txn: Pick<KnetTerminalTxn, 'masked_pan' | 'auth_number' | 'rrn'>): string {
  const pan = txn.masked_pan ? `•••• ${txn.masked_pan.slice(-4)}` : null;
  return [pan, txn.auth_number ? `Auth ${txn.auth_number}` : null, txn.rrn ? `RRN ${txn.rrn}` : null]
    .filter(Boolean)
    .join(' · ');
}

/**
 * Which approved terminal attempts must be reversed when the payment modal
 * closes: those the cashier never put on a submitted order. An attempt that
 * went out on an order (saved, queued offline, or failed and abandoned) is
 * left alone — the server links it or the admin log flags it; reversing it
 * from here could refund a sale that did happen.
 */
export function unusedApprovals(
  splits: Array<{ knetTxnId?: number | undefined }>,
  submitted: ReadonlySet<number>,
): number[] {
  return splits
    .map((s) => s.knetTxnId)
    .filter((id): id is number => id != null && !submitted.has(id));
}
